import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiKey, setApiKey as setStoredApiKey, clearApiKey } from '../api/client';

// Staff email addresses that get admin access via Entra ID login
const STAFF_EMAILS = [
  'jcrawford@edi-partners.com',
];

interface User {
  userId: string;
  userDetails: string;
  roles: string[];
}

interface AuthState {
  user: User | null;
  apiKey: string | null;
  isAuthenticated: boolean;
  isStaff: boolean;
  isLoading: boolean;
  authMode: 'none' | 'apikey' | 'aad';
  
  // Actions
  setUser: (user: User | null) => void;
  setApiKeyAuth: (apiKey: string) => void;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      apiKey: null,
      isAuthenticated: false,
      isStaff: false,
      isLoading: true,
      authMode: 'none',

      setUser: (user) => {
        const hasRoleAccess = user?.roles.includes('ops') || user?.roles.includes('admin') || false;
        const hasEmailAccess = STAFF_EMAILS.some(
          email => user?.userDetails?.toLowerCase() === email.toLowerCase()
        );
        set({
          user,
          isAuthenticated: !!user,
          isStaff: hasRoleAccess || hasEmailAccess,
          isLoading: false,
          authMode: user ? 'aad' : 'none',
        });
      },

      setApiKeyAuth: (apiKey: string) => {
        setStoredApiKey(apiKey);
        set({
          apiKey,
          isAuthenticated: true,
          isStaff: false,
          isLoading: false,
          authMode: 'apikey',
          user: {
            userId: 'api-key-user',
            userDetails: 'API Key Authentication',
            roles: ['tenant'],
          },
        });
      },

      checkAuth: async () => {
        // First check for stored API key
        const storedApiKey = getApiKey();
        if (storedApiKey) {
          set({
            apiKey: storedApiKey,
            isAuthenticated: true,
            isStaff: false,
            isLoading: false,
            authMode: 'apikey',
            user: {
              userId: 'api-key-user',
              userDetails: 'API Key Authentication',
              roles: ['tenant'],
            },
          });
          return;
        }

        // Then check for Azure AD auth
        try {
          const response = await fetch('/.auth/me');
          if (response.ok) {
            const data = await response.json();
            if (data.clientPrincipal) {
              get().setUser({
                userId: data.clientPrincipal.userId,
                userDetails: data.clientPrincipal.userDetails,
                roles: data.clientPrincipal.userRoles || [],
              });
              return;
            }
          }
          get().setUser(null);
        } catch {
          get().setUser(null);
        }
      },

      logout: async () => {
        const { authMode } = get();
        
        if (authMode === 'apikey') {
          clearApiKey();
        }
        
        set({
          user: null,
          apiKey: null,
          isAuthenticated: false,
          isStaff: false,
          isLoading: false,
          authMode: 'none',
        });

        if (authMode === 'aad') {
          // Use full page redirect — fetch() fails due to CORS on SWA identity redirect
          window.location.href = '/.auth/logout?post_logout_redirect_uri=/login';
          return;
        }
        window.location.href = '/login';
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, apiKey: state.apiKey, authMode: state.authMode }),
    }
  )
);

// Check auth on app load
useAuthStore.getState().checkAuth();
